import React, { useContext, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import icons from "../../../assets/images/constantIcons";
import apiService, { handleError } from "../../Common/ApiCommon/ApiService";
import toast from "react-hot-toast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { TimePicker } from 'antd';
import dayjs from "dayjs";
import { UserContext } from "../../Layout/UserContext";

const PayLater = ({ isOpen, onClose, booking_id, fetchPlayers, startTime, tableCharge, tableName, loading }) => {
  const [PlayersData, setPlayersData] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPlayerPopup, setShowPlayerPopup] = useState(false);
  const [isToastShown, setIsToastShown] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  const [showResetPopup, setShowResetPopup] = useState(false);
  const [tableId, setTableId] = useState()
  const { triggerNotificationRefresh } = useContext(UserContext);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);

  // get current time in default
  const getCurrentTime = () => {
    const now = new Date();
    const minutes = now.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 5) * 5;

    if (roundedMinutes === 60) {
      now.setHours(now.getHours() + 1);
      now.setMinutes(0);
    } else {
      now.setMinutes(roundedMinutes);
    }

    let hours = now.getHours();
    const mins = now.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${String(hours).padStart(2, "0")}:${mins} ${ampm}`;
  };

  useEffect(() => {
    if (isOpen) {
      const updatedTime = getCurrentTime();
      formik.setFieldValue("end_time", updatedTime);
    }
  }, [isOpen])

  const formik = useFormik({
    initialValues: {
      previous_amount: "",
      discount: "",
      ratios: [],
      start_time: startTime,
      total_amount: "",
      table_charge: tableCharge,
      today_amount: "",
      payable_ids: [],
      duration: "",
      end_time: getCurrentTime(),
      end_date: new Date().toISOString().split("T")[0],
      totalAmount: ""
    },
    validationSchema: Yup.object({
      payable_ids: Yup.array()
        .of(Yup.number().required("Each payable ID must be a number"))
        .min(1, "At least one payable is required")
        .required("Payable is required"),
      discount: Yup.number().min(0, "Cannot be negative"),
      total_amount: Yup.number().min(0, "Cannot be negative"),
      today_amount: Yup.number()
        .min(0, "Cannot be negative")
        .required("Amount is required"),
      duration: Yup.string().required("Duration is required"),
      end_time: Yup.string().required("End time is required"),
      end_date: Yup.string()
        .required("End date is required")
        .test(
          "is-greater",
          "End date must be greater than Start date",
          function (value) {
            const { payable_id } = this.parent;
            const selectedPlayer = PlayersData?.players?.find(
              (player) => player.id === payable_id
            );
            if (!selectedPlayer || !selectedPlayer.start_date) return true;
            const startDate = new Date(selectedPlayer.start_date.split("T")[0]);
            const endDate = new Date(value);
            return endDate > startDate;
          }
        )
    }),
    onSubmit: async (values) => {

      setIsSubmitting(true);
      // Extract payable_ids and ratios directly from values
      const payable_ids = values.payable_ids;
      const ratios = values.ratios;

      // Set them in values
      values.payable_ids = payable_ids;
      values.ratios = ratios;

      // Find the selected player data
      const selectedPlayer = PlayersData?.players?.find(
        (player) => player.id === payable_ids[0]
      );
      if (selectedPlayer) {
        let { start_date, start_time } = selectedPlayer;
        let { end_date, end_time } = values;
        start_date = start_date.split("T")[0];

        // Convert both times to 24-hour format for comparison
        const convertedStartTime = start_time.includes("AM") || start_time.includes("PM")
          ? apiService.convert12To24Hr(start_time)
          : start_time;

        const convertedEndTime = end_time.includes("AM") || end_time.includes("PM")
          ? apiService.convert12To24Hr(end_time)
          : end_time;

        if (start_date && convertedStartTime && end_date && convertedEndTime) {
          const startDateTime = new Date(`${start_date}T${convertedStartTime}`);
          const endDateTime = new Date(`${end_date}T${convertedEndTime}`);

          if (isNaN(startDateTime) || isNaN(endDateTime)) {
            toast.error("Invalid start or end date/time.");
            setIsSubmitting(false);
            return;
          }

          if (endDateTime <= startDateTime) {
            toast.error("End time must be greater than start time.");
            setIsSubmitting(false);
            return;
          }
        }
      }

      const payload = {
        booking: {
          booking_id,
          payable_ids: payable_ids,
          ratios: ratios,
          end_time: values.end_time,
          duration: values.duration,
          discount: Number(values.discount),
          previous_amount: Number(values.previous_amount),
          today_amount: Number(values.today_amount),
          end_date: values.end_date,
        },
      }
      try {
        const response = await apiService.post("/bookings/proceed_to_payment", payload);
        setTableId(response.data.table_id)
        if (response.status === 200) {
          triggerNotificationRefresh();
          toast.success(response.message, { duration: 2000 });
          setShowResetPopup(true);
        }
      } catch (errors) {
        handleError(errors);
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  useEffect(() => {
    const todayAmount = Number(formik.values.today_amount) || 0;
    const discountAmount = parseFloat(formik.values.discount) || 0;

    // only include previous_amount if one payable is selected
    const previousAmount =
      formik.values.payable_ids.length === 1
        ? Number(formik.values.previous_amount) || 0
        : 0;

    const totalAmount = previousAmount + todayAmount - discountAmount;
    formik.setFieldValue("totalAmount", totalAmount.toFixed(2));
  }, [
    formik.values.discount,
    formik.values.previous_amount,
    formik.values.today_amount,
    formik.values.payable_ids,
  ]);

  // fetch the players
  const fetchPlayer = async () => {
    try {
      const response = await apiService.get(`/bookings/pay_for_booking`, { booking: { booking_id }, });

      const tableCharge = response.data.table_charge || 0;
      const start_date = response.data.start_date;
      const start_time = response.data.start_time;
      // passing  each player booking  table_charge
      const playersWithTableCharge = response.data.players.map((player) => ({
        ...player,
        table_charge: tableCharge,
        start_date: start_date,
        start_time: start_time,
      }));

      setPlayersData({
        ...response.data,
        players: playersWithTableCharge,
      });

      // if only one player, pre-select them and set default ratio
      if (playersWithTableCharge.length === 1) {
        const onlyPlayer = playersWithTableCharge[0];
        formik.setFieldValue("payable_ids", [onlyPlayer.id]);
        formik.setFieldValue("ratios", ["1"]);
        formik.setFieldValue("previous_amount", onlyPlayer.amount);
      }
    } catch (errors) {
      handleError(errors)
    }
  };
  useEffect(() => {
    fetchPlayer();
  }, []);

  useEffect(() => {
    if (formik.values.end_time && formik.values.end_date) {
      const [hours, minutes] = formik.values.end_time.split(":");
      if (hours && minutes) {
        fetchEndTimeData(formik.values.end_time, formik.values.end_date);
      }
    }
  }, [formik.values.end_time, formik.values.end_date]);

  const fetchEndTimeData = async (end_time, end_date) => {
    const formattedEndTime = apiService.convert24To12Hr(end_time);
    try {
      const response = await apiService.get(
        `/bookings/calculate_end_time?booking[booking_id]=${booking_id}&booking[end_time]=${formattedEndTime}&booking[end_date]=${end_date}`
      );
      if (response?.duration && response?.today_amount !== undefined) {
        formik.setFieldValue("duration", response.duration);
        formik.setFieldValue("today_amount", response.today_amount);
        // fetchPlayer();
      }
    } catch (errors) {
      handleError(errors)
    }
  };

  // restart the booking
  const restartBooking = async () => {
    try {
      const allPlayerIds = PlayersData?.players?.map(player => player.id) || [];
      
      const payload = {
        booking: {
          table_id: Number(tableId),
          start_time: formik.values.end_time,
          date: formik.values.end_date,
          players: allPlayerIds,
        },
      };

      const response = await apiService.post("/bookings", payload);
      if (response.status === 200) {
        onClose();
        toast.success(response?.message, { duration: 2000 });
        formik.resetForm();
        fetchPlayers();
      }
    } catch (errors) {
      handleError(errors);
    }
  }
  if (!isOpen) return null;

  if (loading) return <p>Loading ...</p>
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg p-4 s:p-6 w-[310px] s:w-[350px] md:w-[460px] relative">
        {/* Close Button */}
        <div
          className="absolute top-7 right-3 bg-green50 rounded-full p-2 cursor-pointer w-[32px] h-[32px] flex justify-center items-center"
          onClick={() => {
            formik.resetForm();
            onClose();
          }}
        >
          <icons.IoClose className=" text-green700 text-xl" />
        </div>

        <h2 className="text-xl font-semibold text-G500 text-start mb-5">
          Pay Later
        </h2>

        {/* Form */}
        <form
          onSubmit={formik.handleSubmit}
          className="space-y-4 text-start h-[70vh] overflow-y-auto vertical"
        >
          {/* Select Payables with Ratio */}
          <div className="text-G300">
            <label className="block text-sm font-normal mb-2">Payable <span className="text-red ">*</span></label>
            <div className="border border-separate rounded-md p-2 space-y-2 max-h-44 overflow-y-auto vertical">
              <div className="flex justify-between items-center px-2 py-1 border-b border-gray-300 text-xs font-semibold text-gray-600 bg-gray-100">
                <div className="w-52">Player Name</div>
                <div className="w-12 text-right">Ratio</div>
                <div className="w-20 text-right">Amount</div>
              </div>
              {PlayersData?.players?.map((player, index) => {
                const parseRatio = (ratioStr) => {
                  const num = parseInt(ratioStr, 10);
                  return isNaN(num) ? 0 : num;
                };

                const isChecked = formik.values.payable_ids.includes(player.id);
                const currentRatio = formik.values.ratios[index];
                const currentParsedRatio = parseRatio(currentRatio);

                const totalRatio = formik.values.ratios.reduce((sum, ratio) => sum + parseRatio(ratio), 0);

                const ratioAmt =
                  totalRatio > 0 ? (formik.values.totalAmount * currentParsedRatio) / totalRatio : 0;

                const shareAmount = ratioAmt > 0 ? Math.ceil(ratioAmt / 5) * 5 : 0;


                const handleCheckboxChange = (e) => {
                  const updatedPayableIds = e.target.checked
                    ? [...formik.values.payable_ids, player.id]
                    : formik.values.payable_ids.filter((id) => id !== player.id);

                  const updatedRatios = [...formik.values.ratios];

                  if (!e.target.checked) {
                    updatedRatios[index] = null;
                  }

                  formik.setFieldValue("payable_ids", updatedPayableIds);
                  formik.setFieldValue("ratios", updatedRatios);
                };

                const handleRatioChange = (e) => {
                  const value = e.target.value;
                  const validFormat = /^[0-9]{0,2}$/;
                  if (validFormat.test(value)) {
                    const updatedRatios = [...formik.values.ratios];
                    updatedRatios[index] = value;
                    formik.setFieldValue("ratios", updatedRatios);
                  }
                };

                return (
                  <div key={player.id} className="flex flex-col">
                    <div className="flex justify-between items-center border rounded-md px-2 py-[2px] bg-gray-50">
                      <label className="flex items-center text-sm font-medium">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={handleCheckboxChange}
                          className="mr-2"
                        />
                        {player.name}
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="text"
                          placeholder="2"
                          className="w-12 text-sm px-2 py-[2px] border border-G80 rounded-lg outline-none"
                          value={currentRatio}
                          onChange={handleRatioChange}
                          disabled={formik.values.payable_ids.length <= 1 || !isChecked}
                          onKeyPress={(e) => {
                            const allowedChars = /[0-9]/;
                            if (!allowedChars.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                        />
                        <p className="text-sm font-medium text-green w-20 text-right">₹{shareAmount}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {formik.touched.payable_ids && formik.errors.payable_ids && (
              <div className="text-red text-sm">{formik.errors.payable_ids}</div>
            )}

            {formik.values.payable_ids.length > 0 && (
              <div
                className="text-sm text-green hover:text-blue hover:underline mt-1"
                onMouseEnter={() => setShowPlayerPopup(true)}
                onMouseLeave={() => setShowPlayerPopup(false)}
              >
                view booking details
              </div>
            )
            }
          </div>


          {showPlayerPopup && (
            <div className="absolute bg-gray-50 p-2 shadow-md border rounded-md w-auto z-30">
              {/* Table Info Section */}
              <div className="mb-3">
                <p className="text-sm"><strong>Table Name:</strong> <span className="pl-2">{tableName}</span></p>
                <p className="text-sm"><strong>Table Charge:</strong> <span className="pl-2">₹{tableCharge}</span></p>
                <p className="text-sm"><strong>Start Time:</strong> <span className="pl-2">{startTime}</span></p>
              </div>
              {/* Player Info Table */}
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100 text-left">
                    <th className="border px-3 py-1">Player</th>
                    <th className="border px-3 py-1">Previous Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {PlayersData?.players
                    ?.filter((player) => formik.values.payable_ids.includes(player.id))
                    .map((player) => (
                      <tr key={player.id}>
                        <td className="border px-3 py-1">{player.name}</td>
                        <td className="border px-3 py-1">₹{Math.trunc(player.amount)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {/* end date */}
          <div className="text-G300 ">
            <label className="block text-sm font-normal mb-2">End Date <span className="text-red ">*</span></label>
            <div className="relative">
              <DatePicker
                selected={
                  formik.values.end_date ? new Date(formik.values.end_date) : null
                }
                onChange={(end_date) =>
                  formik.setFieldValue(
                    "end_date",
                    end_date.toISOString().split("T")[0]
                  )
                }
                minDate={(() => {
                  const yesterday = new Date();
                  yesterday.setDate(yesterday.getDate() - 1);
                  yesterday.setHours(0, 0, 0, 0);
                  return yesterday;
                })()}
                maxDate={(() => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return today;
                })()}
                dateFormat="yyyy-MM-dd"
                className="inputtext placeholder:text-G50 w-full pr-10"
              />
              <icons.MdOutlineCalendarMonth className="absolute right-3 top-3 text-green pointer-events-none" />
            </div>

            {formik.errors.end_date && formik.touched.end_date && (
              <div className="text-red text-sm">{formik.errors.end_date}</div>
            )}
          </div>
          {/* End Time */}

          <div className="text-G300 flex  items-start flex-col">
            <label className="block text-sm font-normal mb-2">
              End Time <span className="text-red ">*</span>
            </label>
            <div className="w-full">
              <TimePicker
                value={formik.values.end_time ? dayjs(formik.values.end_time, "hh:mm A") : null}
                format="hh:mm A"
                use12Hours
                minuteStep={5}
                allowClear={false}
                inputReadOnly={true}
                showNow={true}
                renderExtraFooter={() => null}
                className="inputtext w-full focus:outline-none outline-none"
                onSelect={(time) => {
                  if (time) {
                    formik.setFieldValue("end_time", time.format("hh:mm A"));
                  }
                }}
                popupClassName="custom-timepicker-popupok"
                getPopupContainer={() => document.body}
              />
            </div>

            {formik.touched.end_time && formik.errors.end_time && (
              <div className="text-red text-sm">
                {formik.errors.end_time}
              </div>
            )}
          </div>

          {/* Duration */}
          <div className="text-G300">
            <label className="block text-sm font-normal mb-2 text-start">
              Duration <span className="text-red ">*</span>
            </label>
            <input
              type="text"
              name="duration"
              className="inputtext"
              placeholder="HH:MM"
              value={formik.values.duration}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled
            />
            {formik.touched.duration && formik.errors.duration && (
              <div className="text-red text-sm text-start">
                {formik.errors.duration}
              </div>
            )}
          </div>

          {/* Amount */}
          <div className="text-G300">
            <label className="block text-sm font-normal mb-2 text-start">
              Today Amount <span className="text-red ">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-3 flex items-center text-green font-semibold">₹</span>
              <input
                type="number"
                name="today_amount"
                className="inputtext text-green"
                placeholder="₹ 0"
                style={{ paddingLeft: "25px" }}
                value={Math.trunc(formik.values.today_amount)}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled
              />
            </div>
            {formik.touched.today_amount && formik.errors.today_amount && (
              <div className="text-red text-sm text-start">
                {formik.errors.today_amount}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between mt-4">
            <label className="text-sm font-normal text-G500">Want to Add a Discount ?</label>
            <button
              type="button"
              className={`px-3 py-1 rounded-full text-sm border ${showDiscount ? 'bg-green text-white' : 'bg-white text-green border-green'}`}
              onClick={() => setShowDiscount(!showDiscount)}
            >
              {showDiscount ? 'Remove' : 'Add'}
            </button>
          </div>

          {/* Discount Amount */}
          {showDiscount && (
            <div className="text-G300">
              <label className="block text-sm font-normal mb-2 text-start">
                Discount Amount (if any)
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-3 flex items-center text-green font-semibold">₹</span>
                <input
                  type="number"
                  name="discount"
                  className="inputtext text-green placeholder:text-green"
                  placeholder="0"
                  style={{ paddingLeft: "25px" }}
                  value={formik.values.discount}
                  onChange={(e) => {
                    let value = e.target.value;
                    // Prevent negative input
                    if (value.includes("-")) {
                      value = "0"; // Reset to 0 if user tries to enter "-"
                    }
                    let numericValue = value === "" ? "" : parseFloat(value) || 0;

                    const previousAmount =
                      formik.values.payable_ids.length === 1
                        ? Number(formik.values.previous_amount) || 0
                        : 0;
                    const totalAmount =
                      previousAmount + (Number(formik.values.today_amount) || 0);

                    if (numericValue > totalAmount) {
                      if (!isToastShown) {
                        setIsToastShown(true);
                        toast.error(
                          "Discount cannot be greater than the total amount",
                          { duration: 2000, onClose: () => setIsToastShown(false), });
                      }
                    } else {
                      setIsToastShown(false);
                      formik.setFieldValue("discount", numericValue);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "-") { e.preventDefault() }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === "") {
                      formik.setFieldValue("discount", 0);
                    }
                  }}
                  onWheel={(e) => e.target.blur()} // Prevent number scrolling by removing focus
                />
              </div>
              {formik.touched.discount && formik.errors.discount && (
                <div className="text-red text-sm text-start">{formik.errors.discount} </div>
              )}
            </div>
          )}
          {/* Total amount */}
          <div className="text-G300">
            <label className="block text-sm font-normal mb-2 text-start">
              Total Amount (Prev + Today) <span className="text-red ">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-3 flex items-center text-green font-semibold">₹</span>
              <input
                type="number"
                name="total_amount"
                className="inputtext text-green"
                placeholder="0"
                style={{ paddingLeft: "25px" }}
                value={Math.trunc(formik.values.totalAmount)}
                onBlur={formik.handleBlur}
                disabled
              />
            </div>
            {formik.touched.total_amount && formik.errors.total_amount && (
              <div className="text-red text-sm text-start">
                {formik.errors.total_amount}
              </div>
            )}
          </div>
      
          {/* Buttons */}
          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={() => {
                formik.resetForm();
                onClose();
                fetchPlayers();
              }}
              className="w-[75px] sm:w-[93px] py-2 text-center text-sm bg-white text-green rounded-lg border border-green"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={async () => {
                const errors = await formik.validateForm();
                if (Object.keys(errors).length === 0) {
                  setShowConfirmPopup(true);  // No errors — open confirmation popup
                } else {
                  formik.setTouched(
                    Object.keys(formik.initialValues).reduce((acc, key) => {
                      acc[key] = true;
                      return acc;
                    }, {})
                  );
                }
              }}
              className={`w-[75px] sm:w-[93px] text-sm text-center py-2 rounded-lg ${isSubmitting ? "bg-green50 cursor-not-allowed" : "bg-green text-white"
                }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Confirming..." : "Confirm"}
            </button>

          </div>
        </form>
      </div >

      {showConfirmPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[300px] shadow-lg">
            <h2 className="text-lg font-semibold mb-4 text-center">Are you sure you want to confirm this?</h2>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => {
                  setShowConfirmPopup(false);
                  formik.handleSubmit();
                }}
                className="w-[75px] py-2 text-sm text-white bg-green rounded-lg"
              >
                Yes
              </button>
              <button
                onClick={() => setShowConfirmPopup(false)}
                className="w-[75px] py-2 text-sm bg-white text-green border border-green rounded-lg"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* restart popup */}
      {showResetPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-60">
          <div className="bg-white rounded-lg p-6 w-90 text-center space-y-4">
            <h2 className="text-lg font-semibold text-G500 mb-2">Payment Successful!</h2>
            <p className="text-sm text-G300">Do you want to Restart Booking or Close ?</p>
            <div className="flex justify-center gap-3 mt-4">
              <button
                className="bg-green text-white px-4 py-2 rounded"
                onClick={() => {
                  restartBooking();
                  setShowResetPopup(false);
                }}
              >
                Restart
              </button>
              <button
                className="bg-gray-200 text-G500 px-4 py-2 rounded"
                onClick={() => {
                  formik.resetForm();
                  setShowResetPopup(false);
                  onClose();
                  fetchPlayers();
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PayLater;
